// ShipPane.tsx — Detail panel for a selected freighter: its designation + type, status
// and speed (performance), crew, route, and cargo manifest. Shown in place of the system
// panel while a ship is selected. The data is the wire Ship (server-authoritative).
import type { Galaxy } from '../types';
import type { Ship } from '../state/economy';
import { useGame } from '../state/context';
import { shipName, shipType } from '../scene/shipNames';

const SHIP_CAPACITY = 30000; // mirrors ShipCapacity in server/sim/ship.go

function formatQty(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
}

function planetName(galaxy: Galaxy, id: string): string {
  for (const s of galaxy.systems) for (const p of s.planets) if (p.id === id) return p.name;
  return id;
}
function systemOfPlanet(galaxy: Galaxy, id: string): string {
  for (const s of galaxy.systems) if (s.planets.some((p) => p.id === id)) return s.name;
  return '';
}
function systemName(galaxy: Galaxy, id: string): string {
  return galaxy.systems.find((s) => s.id === id)?.name ?? id;
}
function routeEndpoint(galaxy: Galaxy, planetId: string): string {
  const p = planetName(galaxy, planetId);
  const sys = systemOfPlanet(galaxy, planetId);
  return sys && sys !== p ? `${p} · ${sys}` : p;
}

export function ShipPane({ galaxy, ship }: { galaxy: Galaxy; ship: Ship }) {
  const { selectShip } = useGame();
  const speed = Math.hypot(ship.velLocal[0], ship.velLocal[1], ship.velLocal[2]) * 86400; // units/day
  const cargo = Object.entries(ship.cargo)
    .filter(([, q]) => q > 0)
    .sort((a, b) => b[1] - a[1]);
  const cargoTotal = cargo.reduce((a, [, q]) => a + q, 0);
  const status =
    ship.phase === 'hyperlane'
      ? `FTL transit → ${systemName(galaxy, ship.destId)}`
      : ship.phase === 'orbit'
        ? `In orbit · ${planetName(galaxy, ship.originId)}`
        : 'In transit';

  return (
    <aside className="system-panel">
      <button className="back-btn" onClick={() => selectShip(null)}>
        ← Close
      </button>
      <h1>{shipName(ship)}</h1>
      <div className="spectral">{shipType(ship.kind)}</div>
      <dl className="stat-list">
        <div className="stat">
          <dt>Status</dt>
          <dd>{status}</dd>
        </div>
        <div className="stat">
          <dt>Bound for</dt>
          <dd>{planetName(galaxy, ship.targetId)}</dd>
        </div>
        <div className="stat">
          <dt>Speed</dt>
          <dd>{ship.phase === 'hyperlane' ? '10× c' : `${speed >= 1 ? speed.toFixed(1) : speed.toFixed(2)} u/day`}</dd>
        </div>
        <div className="stat">
          <dt>Crew</dt>
          <dd>
            {ship.pops} pop{ship.pops === 1 ? '' : 's'}
          </dd>
        </div>
        <div className="stat">
          <dt>Hold</dt>
          <dd>
            {formatQty(cargoTotal)} / {formatQty(SHIP_CAPACITY)}
          </dd>
        </div>
      </dl>

      <div className="planet-count">Route</div>
      <ul className="moon-list">
        <li className="moon-row" style={{ cursor: 'default' }}>
          <span className="dot" style={{ background: '#7fe0c0', color: '#7fe0c0' }} />
          <span className="mname">{routeEndpoint(galaxy, ship.routeA)}</span>
          <span className="mmeta">⇄</span>
        </li>
        <li className="moon-row" style={{ cursor: 'default' }}>
          <span className="dot" style={{ background: '#7fe0c0', color: '#7fe0c0' }} />
          <span className="mname">{routeEndpoint(galaxy, ship.routeB)}</span>
          <span className="mmeta" />
        </li>
      </ul>

      <div className="planet-count">Manifest</div>
      {cargo.length > 0 ? (
        <ul className="moon-list">
          {cargo.map(([g, q]) => (
            <li key={g} className="moon-row" style={{ cursor: 'default' }}>
              <span className="dot" style={{ background: '#caa46a', color: '#caa46a' }} />
              <span className="mname" style={{ textTransform: 'capitalize' }}>
                {g}
              </span>
              <span className="mmeta">{formatQty(q)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="readout">Empty hold</div>
      )}
    </aside>
  );
}
